// src/pages/UsersPage.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Button } from '../ui';
import { Settings, Save as SaveIcon, Trash2, UserX } from 'lucide-react';

const client = createAuditedClient('UsersPage');
const ROLE_OPTIONS = [
  { value: 'country_admin', label: 'Country admin (inbox + edit)' },
  { value: 'editor', label: 'Editor (edit, no inbox)' },
  { value: 'staff', label: 'Staff (read-only)' },
];
/* ================= GraphQL ================= */
// Agencies (Company model)
const ListCompanies = /* GraphQL */ `
  query ListCompanies {
    listCompanies(limit: 1000) {
      items {
        id
        name
        country 
        currencies
        pickupLocations
        dropoffLocations
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const CreateCompany = /* GraphQL */ `
  mutation CreateCompany($input: CreateCompanyInput!) {
    createCompany(input: $input) {
      id
      name
      country 
      currencies
      pickupLocations
      dropoffLocations
      createdAt
      updatedAt
    }
  }
`;

const UpdateCompany = /* GraphQL */ `
  mutation UpdateCompany($input: UpdateCompanyInput!) {
    updateCompany(input: $input) {
      id
      name
      country 
      currencies
      pickupLocations
      dropoffLocations
      updatedAt
    }
  }
`;

const DeleteCompany = /* GraphQL */ `
  mutation DeleteCompany($input: DeleteCompanyInput!) {
    deleteCompany(input: $input) { id }
  }
`;

// Employees / admins
const InviteEmployee = /* GraphQL */ `
  mutation InviteEmployee($input: InviteEmployeeInput!) {
    inviteEmployee(input: $input) { sub email role }
  }
`;

// Members
const ListMembers = /* GraphQL */ `
  query ListMembers($filter: ModelMemberFilterInput, $limit: Int, $nextToken: String) {
    listMembers(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items { id CompanyId sub email role canEditCalendar createdAt updatedAt }
      nextToken
    }
  }
`;

const DeleteMember = /* GraphQL */ `
  mutation DeleteMember($input: DeleteMemberInput!) {
    deleteMember(input: $input) { id }
  }
`;

const SetUserGroup = /* GraphQL */ `
  mutation SetUserGroup($input: SetUserGroupInput!) {
    setUserGroup(input: $input) { sub newGroup prevGroups }
  }
`;

const DeleteUser = /* GraphQL */ `
  mutation DeleteUser($input: DeleteUserInput!) {
    deleteUser(input: $input) {
      sub
      email
      username
    }
  }
`;

const COUNTRY_OPTIONS = [
  { label: 'Lebanon', value: 'Lebanon' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Albania', value: 'Albania' },
  { label: 'Germany', value: 'Germany' },
  { label: 'United Arab Emirates', value: 'United Arab Emirates' },
];

export default function UsersPage() {
  const [claims, setClaims] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [companies, setCompanies] = useState([]);
  // Map companyId => Member[]
  const [membersByCo, setMembersByCo] = useState(new Map());
  const [openCoId, setOpenCoId] = useState(null);

  const isAdmin = useMemo(() => !!claims?.groups?.includes('admin'), [claims]);

  /* ===== Toast + Confirm (shared) ===== */
  const [toast, setToast] = useState(null); // { message, variant }
  const toastTimeoutRef = useRef(null);

  const [confirmState, setConfirmState] = useState(null); // { title, message, confirmLabel, cancelLabel }
  const confirmResolveRef = useRef(null);

  function showAlert(message, variant = 'info') {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, 3500);
  }

  function hideAlert() {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }

  function openConfirm(options) {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({
        title: options.title || 'Confirm action',
        message: options.message || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
      });
    });
  }

  function handleConfirm(result) {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(result);
      confirmResolveRef.current = null;
    }
    setConfirmState(null);
  }

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  /* ===== Auth bootstrap ===== */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await getCurrentUser();
        const { tokens } = await fetchAuthSession({ forceRefresh: true });
        const p = tokens?.idToken?.payload || {};
        const CompanyId = p['custom:CompanyId'] ?? p['custom:companyId'] ?? null;
        const groups = p['cognito:groups'] || [];
        if (!alive) return;
        setClaims({ CompanyId, groups, sub: p.sub || null });
      } catch {
        if (!alive) return;
        setClaims({ CompanyId: null, groups: [] });
      } finally {
        if (alive) setAuthReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* ===== Load agencies ===== */
  useEffect(() => {
    (async () => {
      if (!authReady || !isAdmin) return;
      try {
        const res = await client.graphql({ query: ListCompanies, authMode: 'userPool' });
        const items = (res.data?.listCompanies?.items || [])
          .filter(Boolean)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCompanies(items);
      } catch (e) {
        console.error('ListCompanies failed:', e);
        setCompanies([]);
        showAlert('Failed to load agencies. Check console/logs.', 'error');
      }
    })();
  }, [authReady, isAdmin]);

  /* ===== Members helpers ===== */
  async function loadMembersForCompany(companyId) {
    try {
      const res = await client.graphql({
        query: ListMembers,
        variables: { filter: { CompanyId: { eq: companyId } }, limit: 1000 },
        authMode: 'userPool',
      });
      const items = (res.data?.listMembers?.items || [])
        .filter(Boolean)
        .sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      setMembersByCo(prev => {
        const next = new Map(prev);
        next.set(companyId, items);
        return next;
      });
    } catch (e) {
      console.error('ListMembers failed for', companyId, e);
      setMembersByCo(prev => {
        const next = new Map(prev);
        next.set(companyId, []);
        return next;
      });
      showAlert('Failed to load members. Check console/logs.', 'error');
    }
  }

  async function handleChangeMemberRole(companyId, member, newRole) {
    const role = newRole || 'staff';
    const canEditCalendar = role === 'country_admin' || role === 'editor';

    // Optimistic UI
    setMembersByCo(prev => {
      const next = new Map(prev);
      const list = (next.get(companyId) || []).map(m =>
        m.id === member.id ? { ...m, role, canEditCalendar } : m
      );
      next.set(companyId, list);
      return next;
    });

    try {
      await client.graphql({
        query: SetUserGroup,
        variables: { input: { sub: member.sub, group: role } },
        authMode: 'userPool',
      });

      // reload to reflect canonical state from backend
      await loadMembersForCompany(companyId);

      showAlert('User group updated. User must re-login to get new permissions.', 'success');
    } catch (e) {
      console.error('Change member role failed:', e);
      // rollback
      setMembersByCo(prev => {
        const next = new Map(prev);
        const list = (next.get(companyId) || []).map(m =>
          m.id === member.id ? member : m
        );
        next.set(companyId, list);
        return next;
      });
      showAlert('Failed to change user group. Check console/logs.', 'error');
    }
  }


  /* ===== UI guards ===== */
  if (!authReady) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ fontSize: 14, color: '#6b7280' }}>Loading…</div>

        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
        {confirmState && <ConfirmDialog state={confirmState} onResult={handleConfirm} />}
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <b>Not authorized.</b>
        {toast && <ToastBox toast={toast} onClose={hideAlert} />}
        {confirmState && <ConfirmDialog state={confirmState} onResult={handleConfirm} />}
      </div>
    );
  }

  return (
    <div style={{ background: '#f3f4f6', minHeight: 'calc(100vh - 64px)' }}>
      <div
        style={{
          width: '100%',
          maxWidth: 960,
          margin: '0 auto',
          padding: isMobile ? '12px 10px 24px' : '16px 12px 32px',
          display: 'grid',
          gap: 18,
          boxSizing: 'border-box',
        }}
      >
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 22 }}>Users & Agencies</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              Manage agencies, employees, and who can edit calendars.
            </p>
          </div>
          <span
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              padding: '4px 8px',
              borderRadius: 999,
              border: '1px solid #d1d5db',
              background: '#e5f7da',
              color: '#166534',
              whiteSpace: 'nowrap',
            }}
          >
            Admin only
          </span>
        </header>

        {/* Create agency (no user) */}
        <section style={cardStyle()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <h4 style={{ margin: 0, fontSize: 16 }}>Create an Agency</h4>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Step 1</span>
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280' }}>
            Create an agency record. You can invite admins and employees afterward.
          </p>
          <StartAgencyForm
            onDone={async (co) => {
              if (!co) return;
              setCompanies(prev =>
                [...prev, co]
                  .filter(Boolean)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              );
              await loadMembersForCompany(co.id);
              showAlert('Agency created.', 'success');
            }}
            onNotify={showAlert}
          />
        </section>

        {/* Invite employee / agency admin */}
        <section style={cardStyle()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <h4 style={{ margin: 0, fontSize: 16 }}>Invite User to Agency</h4>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Step 2</span>
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280' }}>
            Invite users into an existing agency as either employees or agency admins.
          </p>
          <InviteEmployeeForm
            companies={companies}
            onInvited={async (companyId) => {
              if (companyId) await loadMembersForCompany(companyId);
            }}
            onNotify={showAlert}
          />
        </section>

        {/* Agencies + Members admin */}
        <section style={cardStyle()}>
          <h4 style={{ marginTop: 0, marginBottom: 6, fontSize: 16 }}>Agencies</h4>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280' }}>
            Configure each agency: name, supported currencies, pickup & dropoff locations, and members.
          </p>
          {companies.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: 13, paddingTop: 4 }}>
              (No agencies yet)
            </div>
          )}
          <div style={{ display: 'grid', gap: 10 }}>
            {companies.map(co => (
              <CompanyAdminCard
                key={co.id}
                company={co}
                isMobile={isMobile}
                isOpen={openCoId === co.id}
                onToggleOpen={async () => {
                  setOpenCoId(p => (p === co.id ? null : co.id));
                  if (membersByCo.get(co.id) == null) await loadMembersForCompany(co.id);
                }}
                members={membersByCo.get(co.id) || []}
                onReloadMembers={() => loadMembersForCompany(co.id)}
                onUpdateCompany={async (update) => {
                  try {
                    const res = await client.graphql({
                      query: UpdateCompany,
                      variables: { input: { id: co.id, ...update } },
                      authMode: 'userPool',
                    });
                    const updated = res.data?.updateCompany;
                    setCompanies(prev =>
                      prev
                        .map(x => (x.id === co.id ? { ...x, ...updated } : x))
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    );
                    showAlert('Agency updated.', 'success');
                  } catch (e) {
                    console.error('UpdateCompany failed:', e);
                    showAlert('Update failed. Check console.', 'error');
                  }
                }}
                onDelete={async () => {
                  const confirmed = await openConfirm({
                    title: 'Delete agency',
                    message: `Delete agency "${co.name}"? This does NOT cascade delete members.`,
                    confirmLabel: 'Delete agency',
                    cancelLabel: 'Cancel',
                  });
                  if (!confirmed) return;

                  try {
                    await client.graphql({
                      query: DeleteCompany,
                      variables: { input: { id: co.id } },
                      authMode: 'userPool',
                    });
                    setCompanies(prev => prev.filter(x => x.id !== co.id));
                    setMembersByCo(prev => {
                      const next = new Map(prev);
                      next.delete(co.id);
                      return next;
                    });
                    if (openCoId === co.id) setOpenCoId(null);
                    showAlert('Agency deleted.', 'success');
                  } catch (e) {
                    console.error('DeleteCompany failed:', e);
                    showAlert('Delete failed. Check console.', 'error');
                  }
                }}
                onMemberChangeRole={(m, newRole) => handleChangeMemberRole(co.id, m, newRole)}
                onMemberDelete={async (m) => {
                  const confirmed = await openConfirm({
                    title: 'Delete user',
                    message: `Delete user ${m.email}? This will remove them from Cognito as well.`,
                    confirmLabel: 'Delete user',
                    cancelLabel: 'Cancel',
                  });
                  if (!confirmed) return;

                  try {
                    // 1) Delete Member row
                    await client.graphql({
                      query: DeleteMember,
                      variables: { input: { id: m.id } },
                      authMode: 'userPool',
                    });

                    // 2) Delete Cognito user (using their sub)
                    if (m.sub) {
                      await client.graphql({
                        query: DeleteUser,
                        variables: { input: { sub: m.sub } },
                        authMode: 'userPool',
                      });
                    } else {
                      console.warn('Member has no sub – skipping Cognito delete');
                    }

                    await loadMembersForCompany(co.id);
                    showAlert('User deleted from system and Cognito.', 'success');
                  } catch (e) {
                    console.error('Delete user failed:', e);
                    showAlert('Delete failed. Check console.', 'error');
                  }
                }}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Toast alert */}
      {toast && <ToastBox toast={toast} onClose={hideAlert} />}

      {/* Confirm dialog */}
      {confirmState && <ConfirmDialog state={confirmState} onResult={handleConfirm} />}
    </div>
  );
}

/* ---------------- Toast / Confirm components ---------------- */

function ToastBox({ toast, onClose }) {
  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 9999,
        maxWidth: 360,
      }}
    >
      <div
        style={{
          borderRadius: 12,
          padding: '10px 12px',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          boxShadow: '0 10px 25px rgba(15,23,42,0.18)',
          border: '1px solid rgba(148,163,184,0.5)',
          background:
            toast.variant === 'success'
              ? '#e7f9ec'
              : toast.variant === 'error'
                ? '#fef2f2'
                : '#f9fafb',
          color:
            toast.variant === 'success'
              ? '#166534'
              : toast.variant === 'error'
                ? '#991b1b'
                : '#111827',
          fontSize: 13,
        }}
      >
        <div style={{ fontSize: 16 }}>
          {toast.variant === 'success' ? '✓' : toast.variant === 'error' ? '!' : 'ℹ'}
        </div>
        <div style={{ flex: 1 }}>{toast.message}</div>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            margin: 0,
            fontSize: 14,
            lineHeight: 1,
            color: 'inherit',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({ state, onResult }) {
  if (!state) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.35)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={() => onResult(false)}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: 14,
          padding: 16,
          minWidth: 280,
          maxWidth: 400,
          boxShadow: '0 20px 40px rgba(15,23,42,0.35)',
          border: '1px solid #e5e7eb',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 style={{ margin: '0 0 6px', fontSize: 16 }}>{state.title}</h4>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#4b5563' }}>
          {state.message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => onResult(false)} style={{ background: 'white' }}>
            {state.cancelLabel}
          </Button>
          <Button onClick={() => onResult(true)} style={{ background: '#dc2626' }}>
            {state.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- UI Components ---------------- */

function cardStyle() {
  return {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
    boxSizing: 'border-box',
  };
}

function rowStyle() {
  return {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)', // label+field stacked for mobile safety
    gap: 6,
    alignItems: 'flex-start',
  };
}

const labelStyle = {
  fontSize: 13,
  color: '#374151',
  fontWeight: 500,
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#f9fafb',
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 80,
  resize: 'vertical',
  fontFamily: 'inherit',
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);

    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);

    setMatches(media.matches);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

function StartAgencyForm({ onDone, onNotify }) {
  const [name, setName] = useState('');
  const [country, setCountry] = useState('Georgia');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    const n = name.trim();
    const c = (country || '').trim() || null;

    if (!n) {
      onNotify?.('Agency name is required.', 'error');
      return;
    }
    if (!c) {
      onNotify?.('Country is required.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const res = await client.graphql({
        query: CreateCompany,
        variables: { input: { name: n, country: c } },
        authMode: 'userPool',
      });
      const agency = res.data?.createCompany || null;
      onDone?.(agency);
      setName('');
      setCountry('Georgia');
    } catch (e2) {
      console.error('createCompany failed:', e2);
      onNotify?.('Failed to create agency. Check console.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 10, maxWidth: '100%' }}>
      <div style={rowStyle()}>
        <label style={labelStyle}>Agency name</label>
        <input
          style={inputStyle}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Haste LLC"
        />
      </div>

      <div style={rowStyle()}>
        <label style={labelStyle}>Country</label>
        <select
          style={inputStyle}
          value={country}
          onChange={e => setCountry(e.target.value)}
        >
          <option value="">Select country…</option>
          {COUNTRY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 4,
          flexWrap: 'wrap',
        }}
      >
        <Button onClick={submit} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create agency'}
        </Button>
      </div>
    </div>
  );
}

function InviteEmployeeForm({ companies, onInvited, onNotify }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [role, setRole] = useState('staff'); // employee | company_admin
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    const e = email.trim().toLowerCase();
    const cid = companyId.trim();
    const r = (role || 'staff').trim() || 'staff';
    if (!e || !cid) {
      onNotify?.('Email and Agency are required.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await client.graphql({
        query: InviteEmployee,
        variables: {
          input: {
            email: e,
            name: name.trim() || null,
            companyId: cid,
            role: r,
          },
        },
        authMode: 'userPool',
      });
      setEmail('');
      setName('');
      setCompanyId('');
      setRole('staff');
      onInvited?.(cid);
      let roleLabel = 'Staff invitation sent.';
      if (r === 'editor') roleLabel = 'Editor invitation sent.';
      if (r === 'country_admin') roleLabel = 'Country admin invitation sent.';
      onNotify?.(roleLabel, 'success')
    } catch (e2) {
      console.error('inviteEmployee failed:', e2);

      let msg = 'Failed to invite user.';
      const appSyncErrors = e2?.errors || e2?.data?.errors;
      if (Array.isArray(appSyncErrors) && appSyncErrors[0]?.message) {
        msg = appSyncErrors[0].message;
      } else if (e2?.message) {
        msg = e2.message;
      }

      onNotify?.(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 10, maxWidth: '100%' }}>
      <div style={rowStyle()}>
        <label style={labelStyle}>Agency</label>
        <select
          style={inputStyle}
          value={companyId}
          onChange={e => setCompanyId(e.target.value)}
        >
          <option value="">Select agency…</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div style={rowStyle()}>
        <label style={labelStyle}>Email</label>
        <input
          style={inputStyle}
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="user@example.com"
        />
      </div>
      <div style={rowStyle()}>
        <label style={labelStyle}>Name</label>
        <input
          style={inputStyle}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Optional"
        />
      </div>
      <div style={rowStyle()}>
        <label style={labelStyle}>Role</label>
        <select
          style={inputStyle}
          value={role}
          onChange={e => setRole(e.target.value)}
        >
          <option value="staff">Staff (read-only)</option>
          <option value="editor">Editor (edit, no inbox)</option>
          <option value="country_admin">Country admin (inbox + edit)</option>
        </select>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 4,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          The selected role maps directly to Cognito groups
          <code>staff</code>, <code>editor</code>, or <code>country_admin</code>.
          Editors and country admins can edit calendars; only country admins see the inbox.
        </span>

        <Button onClick={submit} disabled={submitting}>
          {submitting ? 'Inviting…' : 'Invite user'}
        </Button>
      </div>
    </div>
  );
}

function CompanyAdminCard({
  company,
  isMobile,
  isOpen,
  onToggleOpen,
  members,
  onReloadMembers,
  onUpdateCompany,
  onDelete,
  onMemberChangeRole,
  onMemberDelete,
}) {
  const [editName, setEditName] = useState(company.name);
  const [editCountry, setEditCountry] = useState(company.country || '');
  const [editCurrenciesText, setEditCurrenciesText] = useState(
    (company.currencies || []).join('\n')
  );
  const [editPickupLocationsText, setEditPickupLocationsText] = useState(
    (company.pickupLocations || []).join('\n')
  );
  const [editDropoffLocationsText, setEditDropoffLocationsText] = useState(
    (company.dropoffLocations || []).join('\n')
  );

  useEffect(() => {
    setEditName(company.name);
    setEditCountry(company.country || '');
    setEditCurrenciesText((company.currencies || []).join('\n'));
    setEditPickupLocationsText((company.pickupLocations || []).join('\n'));
    setEditDropoffLocationsText((company.dropoffLocations || []).join('\n'));
  }, [company.name, company.country, company.currencies, company.pickupLocations, company.dropoffLocations]);

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: 10,
        background: '#fafafa',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{company.name}</div>
          <div style={{ color: '#9ca3af', fontSize: 11, wordBreak: 'break-all' }}>
            {company.id}
          </div>
          {company.country && (
            <div style={{ color: '#6b7280', fontSize: 11 }}>{company.country}</div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
          }}
        >
          {/* Manage / expand */}
          <Button
            onClick={onToggleOpen}
            style={{
              background: 'white',
              padding: '4px 6px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              minHeight: 26,
            }}
            title={isOpen ? 'Hide agency settings' : 'Manage agency'}
            aria-label={isOpen ? 'Hide agency settings' : 'Manage agency'}
          >
            <Settings
              size={14}
              style={{
                transition: 'transform 0.15s ease-out',
                transform: isOpen ? 'rotate(90deg)' : 'none',
              }}
            />
          </Button>

          {/* Save + delete */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              onClick={async () => {
                const n = (editName || '').trim();
                const c = (editCountry || '').trim() || null;

                const currencies = (editCurrenciesText || '')
                  .split('\n')
                  .map(s => s.trim())
                  .filter(Boolean);

                const pickupLocations = (editPickupLocationsText || '')
                  .split('\n')
                  .map(s => s.trim())
                  .filter(Boolean);

                const dropoffLocations = (editDropoffLocationsText || '')
                  .split('\n')
                  .map(s => s.trim())
                  .filter(Boolean);

                await onUpdateCompany({
                  name: n || company.name,
                  country: c,
                  currencies,
                  pickupLocations,
                  dropoffLocations,
                });
              }}
              style={{
                background: '#22c55e',
                color: '#111',
                padding: '4px 6px',
                borderRadius: 999,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 28,
                minHeight: 26,
              }}
            >
              <SaveIcon size={14} />
            </Button>

            <Button
              onClick={onDelete}
              style={{
                background: '#ef4444',
                color: 'white',
                padding: '4px 6px',
                borderRadius: 999,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 28,
                minHeight: 26,
              }}
              title="Delete agency"
              aria-label="Delete agency"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>

      </div>

      {isOpen && (
        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          <div style={rowStyle()}>
            <label style={labelStyle}>New agency name</label>
            <input
              style={inputStyle}
              value={editName}
              onChange={e => setEditName(e.target.value)}
            />
          </div>
          <div style={rowStyle()}>
            <label style={labelStyle}>Country</label>
            <select
              style={inputStyle}
              value={editCountry || ''}
              onChange={e => setEditCountry(e.target.value)}
            >
              <option value="">Select country…</option>
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div style={rowStyle()}>
            <label style={labelStyle}>Supported currencies (one per line)</label>
            <textarea
              rows={3}
              style={textareaStyle}
              value={editCurrenciesText}
              onChange={e => setEditCurrenciesText(e.target.value)}
              placeholder={'e.g.\nUSD\nEUR\nGBP'}
            />
          </div>

          <div style={rowStyle()}>
            <label style={labelStyle}>Pickup locations (one per line)</label>
            <textarea
              rows={3}
              style={textareaStyle}
              value={editPickupLocationsText}
              onChange={e => setEditPickupLocationsText(e.target.value)}
              placeholder={'e.g.\nAirport'}
            />
          </div>

          <div style={rowStyle()}>
            <label style={labelStyle}>Dropoff locations (one per line)</label>
            <textarea
              rows={3}
              style={textareaStyle}
              value={editDropoffLocationsText}
              onChange={e => setEditDropoffLocationsText(e.target.value)}
              placeholder={'e.g.\nAirport'}
            />
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <h5 style={{ margin: 0, fontSize: 14 }}>Members</h5>
              <Button onClick={onReloadMembers} style={{ background: 'white' }}>
                Reload
              </Button>
            </div>

            {isMobile ? (
              <div style={{ marginTop: 6, display: 'grid', gap: 8 }}>
                {members.length === 0 && (
                  <div style={{ padding: 8, color: '#6b7280', fontSize: 13 }}>(no members yet)</div>
                )}
                {members.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 10,
                      background: '#ffffff',
                      display: 'grid',
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflowWrap: 'anywhere' }}>
                      {m.email}
                    </div>
                    <select
                      style={{ ...inputStyle, padding: '8px 10px', fontSize: 12 }}
                      value={m.role || 'staff'}
                      onChange={(e) => onMemberChangeRole(m, e.target.value)}
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      onClick={() => onMemberDelete(m)}
                      style={{
                        background: '#dc2626',
                        color: 'white',
                        padding: '8px 10px',
                        width: '100%',
                        justifyContent: 'center',
                      }}
                      title="Delete user"
                      aria-label="Delete user"
                    >
                      <UserX size={14} style={{ marginRight: 6 }} />
                      Delete user
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  overflowX: 'auto',
                  marginTop: 6,
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    wordWrap: 'break-word',
                  }}
                >
                  <thead>
                    <tr>
                      <th style={th}>Email</th>
                      <th style={th}>Group</th>
                      <th style={th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          style={{
                            padding: 8,
                            color: '#6b7280',
                            fontSize: 13,
                          }}
                        >
                          (no members yet)
                        </td>
                      </tr>
                    )}
                    {members.map((m, idx) => (
                      <tr
                        key={m.id}
                        style={{
                          borderTop: '1px solid #f3f4f6',
                          background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                        }}
                      >
                        <td style={td}>{m.email}</td>
                        <td style={td}>
                          <select
                            style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }}
                            value={m.role || 'staff'}
                            onChange={(e) => onMemberChangeRole(m, e.target.value)}
                          >
                            {ROLE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td style={td}>
                          <Button
                            onClick={() => onMemberDelete(m)}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              padding: '3px 6px',
                              borderRadius: 999,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 26,
                              minHeight: 24,
                            }}
                            title="Delete user"
                            aria-label="Delete user"
                          >
                            <UserX size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
              Tip: The selected group maps to Cognito groups
              <code>country_admin</code>, <code>editor</code>, or <code>staff</code>.
              Users must re-login for new permissions to take effect.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid #e5e7eb',
  fontWeight: 600,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#6b7280',
  background: '#f9fafb',
};

const td = {
  padding: '6px 8px',
  verticalAlign: 'middle',
  fontSize: 13,
  color: '#111827',
};
